import { Ruler } from "lucide-react";

/**
 * PlumbRuler — mini-painel biomecânico semi-radiológico (vista lateral).
 *
 * Lê `analysis.plumb_line.deviations` (já calculado no backend) e mostra
 * Δcm (quando `heightCm` está disponível) + Δ% por ponto. Sem cálculos
 * adicionais — reusa os deltas existentes.
 *
 * Estética: minimal, premium, pt-PT. Cores subtis (verde/âmbar/vermelho).
 * Mobile-first: empilha em <md, grelha 2-cols em ≥md.
 *
 * Não toca em landmarks, scoring, IA ou backend.
 */

const POINT_PT = {
  ear: "Ouvido (cabeça)",
  shoulder: "Ombro",
  hip: "Anca",
  knee: "Joelho",
  ankle: "Tornozelo",
};

const SHOWN_POINTS = ["ear", "shoulder", "hip", "knee"];

/**
 * `deviations[i].dx` é fração [0..1] do frame (0 = alinhado).
 * Conversão para cm: assumir que a altura do paciente cobre ~95% da altura
 * vertical do frame (margens superior/inferior). Como `dx` é HORIZONTAL e a
 * altura da imagem ≈ height_cm, a conversão é `dx * frame_width_cm`. Em
 * portrait 9:16, frame_width_cm ≈ heightCm × 9/16. Aproximação suficiente
 * para fins clínicos de leitura comparativa.
 */
function toCm(dxFrac, heightCm) {
  if (!Number.isFinite(heightCm) || heightCm <= 0) return null;
  const frameWidthCm = heightCm * (9 / 16);
  return dxFrac * frameWidthCm;
}

function toneFor(status, magnitudePct) {
  if (status === "alinhado" || magnitudePct < 2) return "ok";
  if (status === "moderado" || magnitudePct < 5) return "warn";
  return "alert";
}

const TONE_CLASSES = {
  ok:    "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
  warn:  "border-amber-500/30 bg-amber-500/5 text-amber-300",
  alert: "border-rose-500/35 bg-rose-500/5 text-rose-300",
};

const TONE_DOT = {
  ok:    "bg-emerald-400",
  warn:  "bg-amber-400",
  alert: "bg-rose-400",
};

export default function PlumbRuler({ deviations, heightCm }) {
  if (!Array.isArray(deviations) || deviations.length === 0) return null;
  const rows = deviations
    .filter(d => SHOWN_POINTS.includes((d.point || "").toLowerCase()))
    .map(d => {
      const dxFrac = Number(d.dx) || 0;
      const dxAbs = Math.abs(dxFrac);
      const pct = dxAbs * 100;
      const cm = toCm(dxAbs, heightCm);
      const tone = toneFor(d.status, pct);
      const dirLabel = dxFrac > 0.005 ? "anterior" : dxFrac < -0.005 ? "posterior" : "alinhado";
      return {
        key: d.point,
        label: POINT_PT[d.point] || d.point,
        pct,
        cm,
        tone,
        dir: dirLabel,
        status: d.status,
      };
    });
  if (!rows.length) return null;

  return (
    <div
      data-testid="plumb-ruler"
      className="rounded-2xl border border-slate-800 bg-slate-950/60 backdrop-blur p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Ruler className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">
          Régua biomecânica · linha de gravidade
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {rows.map(r => (
          <div
            key={r.key}
            data-testid={`plumb-row-${r.key}`}
            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${TONE_CLASSES[r.tone]}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TONE_DOT[r.tone]}`} />
              <span className="text-[12px] text-slate-100 truncate">{r.label}</span>
            </div>
            <div className="flex items-baseline gap-2 font-mono text-[12px]">
              {r.cm != null ? (
                <span data-testid={`plumb-cm-${r.key}`}>{r.cm.toFixed(1)} cm</span>
              ) : (
                <span className="opacity-50">—</span>
              )}
              <span className="opacity-70" data-testid={`plumb-pct-${r.key}`}>
                {r.pct.toFixed(1)}%
              </span>
              <span className="text-[10px] uppercase tracking-[0.12em] opacity-70">{r.dir}</span>
            </div>
          </div>
        ))}
      </div>
      {heightCm == null && (
        <div className="text-[10px] text-slate-500 mt-2 italic">
          Altura do paciente não definida — valores em percentagem do enquadramento.
        </div>
      )}
    </div>
  );
}
